import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';
import i18n from '../../i18n';

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

const ManagerProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUserLang } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [selectedLang, setSelectedLang] = useState('fr');
  const [langSaving, setLangSaving] = useState(false);
  const [langMsg, setLangMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState('');

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const { data: { user: authUser } } = await supabase?.auth?.getUser();
        if (!authUser) { navigate('/login-authentication'); return; }
        setUser(authUser);
        const { data: profileData } = await supabase?.from('users')?.select('*')?.eq('id', authUser?.id)?.single();
        if (profileData) {
          setProfile(profileData);
          setName(profileData?.name || '');
          setPhone(profileData?.phone || '');
          setBio(profileData?.bio || '');
          setSelectedLang(profileData?.lang || 'fr');
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const getInitials = (n) => {
    if (!n) return '?';
    const parts = n?.trim()?.split(' ');
    if (parts?.length >= 2) return (parts?.[0]?.charAt(0) + parts?.[1]?.charAt(0))?.toUpperCase();
    return n?.trim()?.charAt(0)?.toUpperCase();
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const { error } = await supabase?.from('users')?.update({ name, phone, bio })?.eq('id', user?.id);
      if (error) throw error;
      setSaveMsg('✅');
    } catch (err) {
      setSaveMsg('❌');
    } finally {
      setSaving(false);
    }
  };

  const handleLangSave = async () => {
    if (!user) return;
    setLangSaving(true);
    setLangMsg('');
    try {
      const { error } = await supabase?.from('users')?.update({ lang: selectedLang })?.eq('id', user?.id);
      if (error) {
        console.error('Lang save error:', error);
        setLangMsg('❌ ' + error?.message);
        return;
      }
      await i18n?.changeLanguage(selectedLang);
      setLangMsg('✅');
    } catch (err) {
      console.error('Lang save catch:', err);
      setLangMsg('❌ ' + err?.message);
    } finally {
      setLangSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg('');
    if (!newPassword || newPassword?.length < 8) {
      setPasswordMsg('❌ ' + t('profile.minChars'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg('❌ ' + t('profile.passwordsDontMatch'));
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMsg('✅');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg('❌');
    } finally {
      setPasswordSaving(false);
    }
  };

  const inputStyle = {
    backgroundColor: '#111111',
    border: '1px solid #1f1f1f',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    borderRadius: '8px',
    padding: '10px 14px',
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.15s',
  };

  const readonlyInputStyle = { ...inputStyle, color: 'rgba(255,255,255,0.35)', cursor: 'not-allowed' };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.45)',
    fontFamily: 'Sora, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  };

  const sectionStyle = {
    backgroundColor: '#111111',
    border: '1px solid #1f1f1f',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
  };

  const sectionTitleStyle = {
    fontFamily: 'Sora, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    color: 'rgba(255,255,255,0.7)',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #1f1f1f',
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <ManagerSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
        managerName={profile?.name}
      />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <ManagerMobileHeader title={t('profile.title')} showBack={true} />
        <div className="hidden md:flex sticky top-0 z-10 px-4 md:px-6 py-4 border-b items-center gap-3" style={{ backgroundColor: '#0a0a0a', borderColor: '#1f1f1f' }}>
          <div>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('profile.title')}</h1>
          </div>
        </div>
        <div className="px-4 md:px-6 py-4 max-w-2xl">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)]?.map((_, i) => (
                <div key={i} className="h-32 rounded-xl animate-pulse" style={{ backgroundColor: '#111111' }} />
              ))}
            </div>
          ) : (
            <>
              {/* Identité */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('profile.identity')}</p>
                <div className="flex items-center gap-5">
                  <div className="rounded-full flex items-center justify-center font-bold text-white flex-shrink-0" style={{ width: 52, height: 52, backgroundColor: 'var(--color-primary)', fontFamily: 'Sora, sans-serif', fontSize: '18px' }}>
                    {getInitials(name || profile?.name)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{name || profile?.name || 'Manager'}</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{profile?.email}</p>
                  </div>
                </div>
              </div>

              {/* Informations personnelles */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('profile.personalInfo')}</p>
                <div className="space-y-4">
                  <div>
                    <label style={labelStyle}>{t('profile.name')}</label>
                    <input type="text" value={name} onChange={(e) => setName(e?.target?.value)} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('profile.email')}</label>
                    <input type="email" value={profile?.email || ''} readOnly style={readonlyInputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('profile.phone')}</label>
                    <input type="tel" value={phone} onChange={(e) => setPhone(e?.target?.value)} placeholder={t('profile.phonePlaceholder')} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2.5 rounded-lg text-sm font-bold text-white"
                    style={{ backgroundColor: 'var(--color-primary)', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'Sora, sans-serif' }}
                  >
                    {saving ? '...' : t('common.save')}
                  </button>
                  {saveMsg && <span style={{ fontSize: '16px' }}>{saveMsg}</span>}
                </div>
              </div>

              {/* Langue */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('profile.language')}</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {LANGUAGES?.map((lang) => (
                    <button
                      key={lang?.code}
                      onClick={() => setSelectedLang(lang?.code)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '12px 14px', borderRadius: '10px',
                        border: selectedLang === lang?.code ? '1px solid #E5202A' : '1px solid #2a2a2a',
                        backgroundColor: selectedLang === lang?.code ? 'rgba(229,32,42,0.08)' : '#1a1a1a',
                        cursor: 'pointer', color: 'white',
                        fontFamily: 'Inter, sans-serif', fontSize: '14px',
                        fontWeight: selectedLang === lang?.code ? 600 : 400,
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{lang?.flag}</span>
                      <span>{lang?.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleLangSave}
                    disabled={langSaving}
                    className="px-5 py-2.5 rounded-lg text-sm font-bold text-white"
                    style={{ backgroundColor: 'var(--color-primary)', border: 'none', cursor: langSaving ? 'not-allowed' : 'pointer', opacity: langSaving ? 0.7 : 1, fontFamily: 'Sora, sans-serif' }}
                  >
                    {langSaving ? '...' : t('profile.saveLanguage')}
                  </button>
                  {langMsg && <span style={{ fontSize: '16px' }}>{langMsg}</span>}
                </div>
              </div>

              {/* Sécurité */}
              <div style={sectionStyle}>
                <p style={sectionTitleStyle}>{t('profile.security')}</p>
                <div className="space-y-4">
                  <div>
                    <label style={labelStyle}>{t('profile.currentPassword')}</label>
                    <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e?.target?.value)} placeholder="••••••••" style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('profile.newPassword')}</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e?.target?.value)} placeholder={t('profile.minChars')} style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('profile.confirmPassword')}</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e?.target?.value)} placeholder="••••••••" style={inputStyle} onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }} onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }} />
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordSaving}
                    className="px-5 py-2.5 rounded-lg text-sm font-bold"
                    style={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', color: 'white', cursor: passwordSaving ? 'not-allowed' : 'pointer', opacity: passwordSaving ? 0.7 : 1, fontFamily: 'Sora, sans-serif' }}
                  >
                    {passwordSaving ? '...' : t('common.update')}
                  </button>
                  {passwordMsg && <span style={{ fontSize: '13px', color: passwordMsg?.startsWith('❌') ? '#ef4444' : '#22c55e', fontFamily: 'Inter, sans-serif' }}>{passwordMsg}</span>}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <ManagerBottomNav />
    </div>
  );
};

export default ManagerProfile;
